import client from './client';
import { Company, CompanyStatus, PaginatedResponse } from '../types';

export interface CompanyListParams {
  page?: number;
  limit?: number;
  include_pending?: boolean;
  only_pending?: boolean;
  status?: CompanyStatus | 'all';
  search?: string;
}

export const getList = async (params: CompanyListParams = {}): Promise<PaginatedResponse<Company>> => {
  const qs: Record<string, unknown> = {
    page: params.page ?? 1,
    limit: params.limit ?? 50,
  };
  if (params.status) qs.status = params.status;
  if (params.include_pending) qs.include_pending = true;
  if (params.only_pending) qs.only_pending = true;
  if (params.search && params.search.trim()) qs.search = params.search.trim();
  const res = await client.get('/api/v1/companies', { params: qs });
  return res.data;
};

export const getById = async (id: string): Promise<Company> => {
  const res = await client.get(`/api/v1/companies/${id}`);
  return res.data;
};

export interface DuplicateCheckResult {
  exists: boolean;
  is_approved: boolean | null;
  company_id: string | null;
}

export const checkDuplicate = async (name: string): Promise<DuplicateCheckResult> => {
  const res = await client.get('/api/v1/companies/check-duplicate', { params: { name } });
  return res.data;
};

export const create = async (data: {
  name: string;
  business_number?: string;
  phone?: string;
  address?: string;
}): Promise<Company> => {
  const res = await client.post('/api/v1/companies', data);
  return res.data;
};

export const update = async (
  id: string,
  data: { name?: string; business_number?: string; phone?: string; address?: string }
): Promise<Company> => {
  const res = await client.patch(`/api/v1/companies/${id}`, data);
  return res.data;
};

export const remove = async (id: string): Promise<{ message: string; affected_users: number }> => {
  const res = await client.delete(`/api/v1/companies/${id}`);
  return res.data;
};

export const approve = async (id: string): Promise<Company> => {
  const res = await client.post(`/api/v1/companies/${id}/approve`);
  return res.data;
};

export const reject = async (id: string): Promise<Company> => {
  const res = await client.post(`/api/v1/companies/${id}/reject`);
  return res.data;
};

export const recover = async (id: string): Promise<Company> => {
  const res = await client.post(`/api/v1/companies/${id}/recover`);
  return res.data;
};

export const permanentDelete = async (id: string): Promise<{ message: string; affected_users: number }> => {
  const res = await client.delete(`/api/v1/companies/${id}/permanent`);
  return res.data;
};

export interface ChangeUserCompanyBody {
  company_id?: string;
  company_name?: string;
  unset?: boolean;
}

export const changeMyCompany = async (body: ChangeUserCompanyBody) => {
  const res = await client.patch('/api/v1/users/me/company', body);
  return res.data;
};

export const changeUserCompanyByAdmin = async (userId: string, body: ChangeUserCompanyBody) => {
  const res = await client.patch(`/api/v1/users/${userId}/company`, body);
  return res.data;
};
